import * as THREE from 'heritage/assets/three.module.js';

// Round each leaf across its own width, following the actual SVG cutouts.
// This is surface relief only: the identity's outline and holes stay intact.
export function createLaurelRelief(shape, resolution=1024){
  const points=shape.extractPoints(12),rings=[points.shape,...points.holes];
  const box=new THREE.Box2();
  for(const ring of rings)for(const p of ring)box.expandByPoint(p);
  box.expandByScalar(1);
  const width=box.max.x-box.min.x,height=box.max.y-box.min.y;
  const n=resolution,dx=width/n,dy=height/n,mask=new Uint8Array(n*n);
  const edges=rings.flatMap(r=>r.map((p,i)=>[p,r[(i+1)%r.length]]));
  for(let y=0;y<n;y++){
    const py=box.min.y+(y+.5)*dy,crossings=[];
    for(const [a,b] of edges)if((a.y>py)!==(b.y>py))crossings.push(a.x+(py-a.y)*(b.x-a.x)/(b.y-a.y));
    crossings.sort((a,b)=>a-b);
    for(let k=0;k+1<crossings.length;k+=2){
      const first=Math.max(0,Math.ceil((crossings[k]-box.min.x)/dx-.5));
      const last=Math.min(n,Math.ceil((crossings[k+1]-box.min.x)/dx-.5));
      mask.fill(1,y*n+first,y*n+last);
    }
  }
  const distance=Float32Array.from(mask,v=>v?1e5:0),diagonal=Math.hypot(dx,dy);
  for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){
    const i=y*n+x;if(!mask[i])continue;
    distance[i]=Math.min(distance[i],distance[i-1]+dx,distance[i-n]+dy,distance[i-n-1]+diagonal,distance[i-n+1]+diagonal);
  }
  for(let y=n-2;y>0;y--)for(let x=n-2;x>0;x--){
    const i=y*n+x;if(!mask[i])continue;
    distance[i]=Math.min(distance[i],distance[i+1]+dx,distance[i+n]+dy,distance[i+n+1]+diagonal,distance[i+n-1]+diagonal);
  }
  let heights=Float32Array.from(distance,d=>1.35*Math.sqrt(d));
  const kernel=[1,4,6,4,1];
  // Soften medial ridges, producing a rounded crown rather than a fold.
  for(let pass=0;pass<3;pass++)for(const horizontal of [true,false]){
    const blurred=new Float32Array(n*n);
    for(let y=2;y<n-2;y++)for(let x=2;x<n-2;x++){
      let value=0;
      for(let k=-2;k<=2;k++)value+=kernel[k+2]*heights[(y+(horizontal?0:k))*n+x+(horizontal?k:0)];
      blurred[y*n+x]=value/16;
    }
    heights=blurred;
  }
  // Half-float normals keep mirror highlights smooth instead of banding.
  const pixels=new Uint16Array(n*n*4),half=THREE.DataUtils.toHalfFloat;
  for(let y=0;y<n;y++)for(let x=0;x<n;x++){
    const i=y*n+x;
    const gx=(heights[y*n+Math.min(n-1,x+1)]-heights[y*n+Math.max(0,x-1)])/(2*dx);
    const gy=(heights[Math.min(n-1,y+1)*n+x]-heights[Math.max(0,y-1)*n+x])/(2*dy);
    const length=Math.hypot(gx,gy,1);
    pixels.set([half(.5*(1-gx/length)),half(.5*(1-gy/length)),half(.5*(1+1/length)),half(1)],i*4);
  }
  const texture=new THREE.DataTexture(pixels,n,n,THREE.RGBAFormat,THREE.HalfFloatType);
  texture.name='Rounded laurel leaf relief';
  texture.minFilter=texture.magFilter=THREE.LinearFilter;
  texture.repeat.set(1/width,1/height);
  texture.offset.set(-box.min.x/width,-box.min.y/height);
  texture.needsUpdate=true;
  return texture;
}